import { useQuery } from "@tanstack/react-query";
import React, { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { toast } from "react-toastify";

import { PageTitle } from "../../components/Common/PageTitle";
import { InfiniteScroller } from "../../components/InfiniteScroller";
import { EmailForm } from "../../components/SendMessageComponents/EmailForm";
import { ModalConfirmSendMessage } from "../../components/SendMessageComponents/ModalConfirmSendMessage";
import { LocationData } from "../../models/LocationData";
import { EmailTargetType, MassEmailPayload } from "../../models/MassEmail";
import DataSvc from "../../services/dataSvc";
import {
  initialEmailState,
  validateEmailForm,
} from "../SendMessageScreens/utils";

import "./styles.scss";

type IMassSendEmailsPageProps = RouteComponentProps<any>;

const DEFAULT_LIIGO_ADMIN_REPLY_TO = "cs@liigo.world";

const MassSendEmailsPage: React.FunctionComponent<
  IMassSendEmailsPageProps
> = () => {
  const { t: _t } = useTranslation();
  const t = (key: string) => _t(`sendMassEmailsPage.${key}`);

  const [targetType, setTargetType] = useState<EmailTargetType>(
    EmailTargetType.ALL_ORG_USERS
  );
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<
    number[]
  >([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<number[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<LocationData[]>(
    []
  );
  const [sendToError, setSendToError] = useState<string | undefined>(undefined);
  const [
    shownSendMessageConfirmationModal,
    setShownSendMessageConfirmationModal,
  ] = useState<boolean>(false);
  const [sendMessageVariables, setSendMessageVariables] = useState<any>({
    ...initialEmailState,
    userEmail: DEFAULT_LIIGO_ADMIN_REPLY_TO,
  });

  const { messageTitle, messageText, replyEmail, userEmail, bccTo } =
    sendMessageVariables;

  const renderTargetTypeRadio = (
    id: string,
    value: EmailTargetType,
    labelKey: string
  ) => (
    <div className="mass-send__target-type">
      <input
        type="radio"
        id={id}
        name="targetType"
        value={value}
        checked={targetType === value}
        onChange={(e) => {
          setTargetType(e.target.value as EmailTargetType);
          // Clear selected organizations and locations when switching modes
          setSelectedOrganizationIds([]);
          setSelectedLocationIds([]);
          setSelectedLocations([]);
          setSendToError(undefined);
        }}
      />
      <label htmlFor={id}>{t(labelKey)}</label>
    </div>
  );

  // Fetch organizations and locations data
  const { data: organizationsData } = useQuery(
    ["organizations-for-email"],
    () => DataSvc.getOrganizations({ limit: 1000, sort: "name" }),
    {
      select: (data) =>
        data.list.filter((org) => {
          // Filter out expired organizations
          if (!org.plan_expiry_date) return true;
          return new Date(org.plan_expiry_date) > new Date();
        }),
    }
  );

  // Adapter function to transform getLocations response to InfiniteScroller format
  const fetchLocationsForInfiniteScroller = async (params: {
    offset?: number;
    limit?: number;
    sort?: string;
    [key: string]: number | string | undefined;
  }) => {
    const response = await DataSvc.getLocations({
      ...params,
      limit: Math.min(params.limit || 1000, 1000), // Enforce max 1000 limit
      sort: "name",
    });
    return {
      data: response.list,
      count: response.total,
    };
  };

  const updateVariables = (
    event: ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>
  ) => {
    setSendMessageVariables({
      ...sendMessageVariables,
      [event.target.name]: event.target.value,
    });
  };

  const hasSendToError = () => {
    let hasError = false;

    if (targetType === EmailTargetType.SPECIFIC_ORG_USERS) {
      if (selectedOrganizationIds.length === 0) {
        setSendToError(t("please_select_organizations"));
        hasError = true;
      }
    } else if (targetType === EmailTargetType.FACILITY_USERS_OF_ORGS) {
      if (selectedOrganizationIds.length === 0) {
        setSendToError(t("please_select_organizations"));
        hasError = true;
      }
    } else if (targetType === EmailTargetType.SPECIFIC_FACILITY_USERS) {
      if (selectedLocationIds.length === 0) {
        setSendToError(t("please_select_locations"));
        hasError = true;
      }
    }

    if (!hasError) {
      setSendToError(undefined);
    }

    return hasError;
  };

  const onToConfirm = () => {
    validateEmailForm(sendMessageVariables, setSendMessageVariables, () => {
      const sendToError = hasSendToError();
      if (!sendToError) {
        setShownSendMessageConfirmationModal(true);
      }
    });
  };

  // on Send Message
  const onSendMessage = async () => {
    const payload: MassEmailPayload = {
      subject: messageTitle,
      text: messageText,
      replyToEmail: !!replyEmail ? replyEmail : userEmail,
      bccToEmail: !!bccTo ? bccTo : "",
      targetType,
      organizationIds: [],
      locationIds: [],
    };

    if (targetType === EmailTargetType.SPECIFIC_ORG_USERS) {
      payload.organizationIds = selectedOrganizationIds;
    } else if (targetType === EmailTargetType.FACILITY_USERS_OF_ORGS) {
      payload.organizationIds = selectedOrganizationIds;
    } else if (targetType === EmailTargetType.SPECIFIC_FACILITY_USERS) {
      payload.locationIds = selectedLocationIds;
    }

    await DataSvc.sendMassEmail(payload);

    toast(t("email_sent"));
    setSendMessageVariables({
      ...initialEmailState,
      userEmail: DEFAULT_LIIGO_ADMIN_REPLY_TO,
    });
    setSelectedOrganizationIds([]);
    setSelectedLocationIds([]);
    setSelectedLocations([]);
    setShownSendMessageConfirmationModal(false);
  };

  const produceEmailRecepients = () => {
    const targetTypeLabels = {
      [EmailTargetType.ALL_ORG_USERS]: t("target_type.all_org_users"),
      [EmailTargetType.ALL_FACILITY_USERS]: t("target_type.all_facility_users"),
      [EmailTargetType.SPECIFIC_ORG_USERS]: t("target_type.specific_org_users"),
      [EmailTargetType.FACILITY_USERS_OF_ORGS]: t(
        "target_type.facility_users_of_orgs"
      ),
      [EmailTargetType.SPECIFIC_FACILITY_USERS]: t(
        "target_type.specific_facility_users"
      ),
    };

    const targetTypeLabel = targetTypeLabels[targetType];
    const selectedItems = [];

    if (targetType === EmailTargetType.SPECIFIC_ORG_USERS) {
      const selectedOrgNames =
        organizationsData
          ?.filter((org) => selectedOrganizationIds.includes(Number(org.id)))
          ?.map((org) => org.name) || [];
      selectedItems.push(...selectedOrgNames);
    } else if (targetType === EmailTargetType.FACILITY_USERS_OF_ORGS) {
      const selectedOrgNames =
        organizationsData
          ?.filter((org) => selectedOrganizationIds.includes(Number(org.id)))
          ?.map((org) => org.name) || [];
      selectedItems.push(...selectedOrgNames);
    } else if (targetType === EmailTargetType.SPECIFIC_FACILITY_USERS) {
      const selectedLocationNames = selectedLocations.map((loc) => loc.name);
      selectedItems.push(...selectedLocationNames);
    }

    return (
      <div className="mass-send__recipients-display">
        <div className="mass-send__target-type">{targetTypeLabel}</div>
        {selectedItems.length > 0 && (
          <div className="mass-send__selected-items">
            {selectedItems.map((item, index) => (
              <div key={index} className="mass-send__selected-item">
                {item}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="right-content mass-send">
        <div className="top-title-bar flex-grid">
          <PageTitle text={t("mass_send_emails")} />
          <div className="rights"></div>
          <div className="line"></div>
        </div>

        <div className="white-border-panel padding32">
          <div className="mass-send__main">
            <div className="mass-send__target-selection">
              <div className="mass-send__left-panel">
                <h3>{t("select_target_type")}</h3>
                <div className="mass-send__target-types">
                  {renderTargetTypeRadio(
                    "allOrgUsers",
                    EmailTargetType.ALL_ORG_USERS,
                    "target_type.all_org_users"
                  )}
                  {renderTargetTypeRadio(
                    "allFacilityUsers",
                    EmailTargetType.ALL_FACILITY_USERS,
                    "target_type.all_facility_users"
                  )}
                  {renderTargetTypeRadio(
                    "specificOrgUsers",
                    EmailTargetType.SPECIFIC_ORG_USERS,
                    "target_type.specific_org_users"
                  )}
                  {renderTargetTypeRadio(
                    "facilityUsersOfOrgs",
                    EmailTargetType.FACILITY_USERS_OF_ORGS,
                    "target_type.facility_users_of_orgs"
                  )}
                  {renderTargetTypeRadio(
                    "specificFacilityUsers",
                    EmailTargetType.SPECIFIC_FACILITY_USERS,
                    "target_type.specific_facility_users"
                  )}
                </div>
              </div>

              {targetType !== EmailTargetType.ALL_ORG_USERS &&
                targetType !== EmailTargetType.ALL_FACILITY_USERS && (
                  <div className="mass-send__middle-panel">
                    <h3>
                      {targetType === EmailTargetType.SPECIFIC_ORG_USERS ||
                      targetType === EmailTargetType.FACILITY_USERS_OF_ORGS
                        ? t("select_organizations")
                        : targetType === EmailTargetType.SPECIFIC_FACILITY_USERS
                        ? t("select_locations")
                        : t("target_selection_not_required")}
                    </h3>

                    {(targetType === EmailTargetType.SPECIFIC_ORG_USERS ||
                      targetType ===
                        EmailTargetType.FACILITY_USERS_OF_ORGS) && (
                      <div className="mass-send__selection-list">
                        {organizationsData?.map((org) => (
                          <div
                            key={org.id}
                            className="mass-send__selection-item"
                          >
                            <input
                              type="checkbox"
                              id={`org-${org.id}`}
                              checked={selectedOrganizationIds.includes(
                                Number(org.id)
                              )}
                              onChange={(e) => {
                                const orgId = Number(org.id);
                                if (e.target.checked) {
                                  setSelectedOrganizationIds([
                                    ...selectedOrganizationIds,
                                    orgId,
                                  ]);
                                } else {
                                  setSelectedOrganizationIds(
                                    selectedOrganizationIds.filter(
                                      (id) => id !== orgId
                                    )
                                  );
                                }
                              }}
                            />
                            <label htmlFor={`org-${org.id}`}>{org.name}</label>
                          </div>
                        ))}
                      </div>
                    )}

                    {targetType === EmailTargetType.SPECIFIC_FACILITY_USERS && (
                      <div className="mass-send__infinite-scroller-container">
                        <InfiniteScroller
                          fetchFunction={fetchLocationsForInfiniteScroller}
                          initialParams={{ offset: 0, limit: 1000 }}
                          pageSize={1000}
                          dependencyKey="locations-for-mass-email"
                          direction="vertical"
                          className="mass-send__locations-scroller"
                          renderItem={(location: LocationData) => (
                            <div
                              key={location.id}
                              className="mass-send__selection-item"
                            >
                              <input
                                type="checkbox"
                                id={`loc-${location.id}`}
                                checked={selectedLocationIds.includes(
                                  location.id
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedLocationIds([
                                      ...selectedLocationIds,
                                      location.id,
                                    ]);
                                    setSelectedLocations([
                                      ...selectedLocations,
                                      location,
                                    ]);
                                  } else {
                                    setSelectedLocationIds(
                                      selectedLocationIds.filter(
                                        (id) => id !== location.id
                                      )
                                    );
                                    setSelectedLocations(
                                      selectedLocations.filter(
                                        (loc) => loc.id !== location.id
                                      )
                                    );
                                  }
                                }}
                              />
                              <label htmlFor={`loc-${location.id}`}>
                                {location.name}
                              </label>
                            </div>
                          )}
                          noItemsComponent={
                            <div>{t("no_locations_available")}</div>
                          }
                        />
                      </div>
                    )}

                    {sendToError && <div className="error">{sendToError}</div>}
                  </div>
                )}
            </div>
            <EmailForm
              {...sendMessageVariables}
              onChangeTextVariable={updateVariables}
              adminMode={true}
            />
            <div className="mass-send__main__form__btns">
              <button
                className="btn btn-blue"
                onClick={() => {
                  onToConfirm();
                }}
              >
                {t("confirm")}
              </button>
            </div>
          </div>
        </div>
      </div>
      {shownSendMessageConfirmationModal && (
        <ModalConfirmSendMessage
          messageTitle={messageTitle}
          messageText={messageText}
          replyEmail={replyEmail}
          userEmail={DEFAULT_LIIGO_ADMIN_REPLY_TO}
          bccTo={bccTo}
          onClose={() => {
            setShownSendMessageConfirmationModal(false);
          }}
          onToConfirm={() => {
            onSendMessage();
          }}
          conditions={
            <div className="mass-send__conditions">
              <h2 className="top-title">{t("send_to")}</h2>
              {produceEmailRecepients()}
            </div>
          }
        ></ModalConfirmSendMessage>
      )}
    </>
  );
};

export default withRouter(MassSendEmailsPage);
